import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "../../config";
import "./RumblePitchPage.css";

const TOTAL_SLIDES = 11;

function fmt(ms) {
  if (!ms || ms < 1000) return "<1s";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}m ${r}s`;
}

function HeatmapCanvas({ points, width = 600, height = 400 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !points.length) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const offscreen = document.createElement("canvas");
    offscreen.width  = canvas.width;
    offscreen.height = canvas.height;
    const octx = offscreen.getContext("2d");

    for (const p of points) {
      const px = (p.x / 100) * canvas.width;
      const py = (p.y / 100) * canvas.height;
      const grad = octx.createRadialGradient(px, py, 0, px, py, 28);
      grad.addColorStop(0, "rgba(255,106,0,0.08)");
      grad.addColorStop(1, "rgba(255,106,0,0)");
      octx.fillStyle = grad;
      octx.fillRect(px - 30, py - 30, 60, 60);
    }

    // Colorise
    const imgData = octx.getImageData(0, 0, offscreen.width, offscreen.height);
    const out     = ctx.createImageData(imgData.width, imgData.height);
    for (let i = 0; i < imgData.data.length; i += 4) {
      const a = imgData.data[i + 3] / 255;
      out.data[i]     = Math.round(255 * Math.min(1, a * 3));
      out.data[i + 1] = Math.round(100 * Math.max(0, 1 - a * 2));
      out.data[i + 2] = 0;
      out.data[i + 3] = Math.round(a * 220);
    }
    ctx.putImageData(out, 0, 0);
  }, [points]);

  return (
    <div className="pa-heatmap-wrap">
      <div className="pa-heatmap-bg" style={{ width, height }} />
      <canvas ref={canvasRef} width={width} height={height} className="pa-heatmap-canvas" />
      <div className="pa-heatmap-label">Mouse heatmap (all sessions combined)</div>
    </div>
  );
}

export default function PitchAnalytics() {
  const [token,   setToken]   = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const load = async (t = token) => {
    setLoading(true); setError("");
    try {
      const res = await axiosInstance.get(`/pitch/analytics?token=${encodeURIComponent(t)}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    if (t) { setToken(t); load(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slide avg duration map
  const slideStats = (() => {
    if (!data) return {};
    const map = {};
    for (let i = 0; i < TOTAL_SLIDES; i++) map[i] = { total: 0, count: 0 };
    for (const e of data.slides) {
      const idx = e.slideIndex ?? 0;
      if (!map[idx]) map[idx] = { total: 0, count: 0 };
      map[idx].total += e.durationMs || 0;
      map[idx].count += 1;
    }
    return map;
  })();

  const maxSlideAvg = Math.max(1, ...Object.values(slideStats).map(s => s.count ? s.total / s.count : 0));

  const SLIDE_LABELS = [
    "Cover", "The Moment", "The Opportunity", "What Clipbait Is",
    "Two Creator Types", "Campaigns Engine", "Revenue Model",
    "Why Rumble / Why Now", "Competitive Map", "The Ask", "Vision"
  ];

  return (
    <div className="pa-page">
      <div className="pa-header">
        <div className="rp-gate-logo" style={{ marginBottom: 0 }}>
          <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="15" stroke="#FF6A00" strokeWidth="2"/>
            <path d="M11 10 L11 22 L23 16 Z" fill="#FF6A00"/>
          </svg>
          <span className="rp-gate-logo-text" style={{ fontSize: 14 }}>CLIPBAIT × RUMBLE</span>
        </div>
        <h2 className="pa-title">Pitch Analytics Dashboard</h2>
      </div>

      {!data && (
        <div className="pa-auth-wrap">
          <input
            className="pa-token-input"
            type="password"
            placeholder="Analytics token"
            value={token}
            onChange={e => setToken(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
          />
          <button className="rp-gate-btn" style={{ marginTop: 12 }} onClick={() => load()} disabled={loading}>
            {loading ? <span className="rp-gate-spinner" /> : "Load Analytics"}
          </button>
          {error && <div className="rp-gate-error">{error}</div>}
        </div>
      )}

      {data && (
        <>
          {/* ── Summary cards ── */}
          <div className="pa-summary-row">
            <div className="pa-stat-card">
              <div className="pa-stat-val">{data.leads.length}</div>
              <div className="pa-stat-label">Total Viewers</div>
            </div>
            <div className="pa-stat-card">
              <div className="pa-stat-val">{data.clicks.length}</div>
              <div className="pa-stat-label">Total Clicks</div>
            </div>
            <div className="pa-stat-card">
              <div className="pa-stat-val">
                {data.leads.length
                  ? fmt(Math.round(data.leads.reduce((s, l) => s + (l.totalTimeMs || 0), 0) / data.leads.length))
                  : "—"}
              </div>
              <div className="pa-stat-label">Avg Session Time</div>
            </div>
            <div className="pa-stat-card">
              <div className="pa-stat-val">
                {data.leads.length
                  ? Math.round(data.leads.reduce((s, l) => s + (l.slidesViewed?.length || 0), 0) / data.leads.length)
                  : "—"}
              </div>
              <div className="pa-stat-label">Avg Slides Viewed</div>
            </div>
          </div>

          {/* ── Leads table ── */}
          <div className="pa-section">
            <h3 className="pa-section-title">Viewers</h3>
            <div className="pa-table-wrap">
              <table className="pa-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>First Accessed</th>
                    <th>Last Active</th><th>Total Time</th><th>Slides Seen</th><th>Last Slide</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map(l => (
                    <tr key={l._id}>
                      <td>{l.name}</td>
                      <td>{l.email}</td>
                      <td>{new Date(l.createdAt).toLocaleString()}</td>
                      <td>{new Date(l.lastActiveAt).toLocaleString()}</td>
                      <td className="pa-mono">{fmt(l.totalTimeMs)}</td>
                      <td className="pa-mono">{l.slidesViewed?.length ?? 0} / {TOTAL_SLIDES}</td>
                      <td className="pa-mono">{SLIDE_LABELS[l.lastSlide ?? 0] ?? l.lastSlide}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Slide engagement chart ── */}
          <div className="pa-section">
            <h3 className="pa-section-title">Slide Engagement (avg time per slide)</h3>
            <div className="pa-slide-chart">
              {SLIDE_LABELS.map((label, i) => {
                const stat = slideStats[i] || { total: 0, count: 0 };
                const avg  = stat.count ? stat.total / stat.count : 0;
                const pct  = (avg / maxSlideAvg) * 100;
                return (
                  <div key={i} className="pa-slide-row">
                    <div className="pa-slide-num">{i + 1}</div>
                    <div className="pa-slide-name">{label}</div>
                    <div className="pa-slide-bar-bg">
                      <div className="pa-slide-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="pa-slide-time pa-mono">{fmt(avg)}</div>
                    <div className="pa-slide-views pa-mono">{stat.count} views</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Heatmap ── */}
          {data.heatmap.length > 0 && (
            <div className="pa-section">
              <h3 className="pa-section-title">Mouse Heatmap</h3>
              <HeatmapCanvas points={data.heatmap} width={700} height={440} />
            </div>
          )}

          {/* ── Click map ── */}
          {data.clicks.length > 0 && (
            <div className="pa-section">
              <h3 className="pa-section-title">Click Map ({data.clicks.length} clicks)</h3>
              <HeatmapCanvas points={data.clicks} width={700} height={440} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
